import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Calendar,
  Briefcase,
  Smartphone,
  CreditCard,
  ArrowRight,
  FileText,
  Send,
  TrendingUp,
  Clock,
  Users,
  AlertCircle,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  dashboardAPI,
  meAPI,
  type DashboardOverview,
  type ModuleInfo,
} from '../services/api';
import { isFullAccess, userRoles, ROLE_LABEL } from '../utils/roles';
import { Badge, Card, Skeleton, useChartTheme, type Tone } from '../components/ui';
import MonitorDashboard from './dashboards/MonitorDashboard';
import VendedorDashboard from './dashboards/VendedorDashboard';
import PlaceholderDashboard from './dashboards/PlaceholderDashboard';
import ProspectDashboard from './prospect/Dashboard';

const MODULE_META: Record<string, { icon: React.ReactNode; to: string }> = {
  agendamento: { icon: <Calendar size={24} />, to: '/agendamento' },
  prospect: { icon: <Briefcase size={24} />, to: '/prospect' },
  central: { icon: <Smartphone size={24} />, to: '/central' },
};

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Em teste',
  active: 'Ativa',
  past_due: 'Atrasada',
  canceled: 'Cancelada',
  unpaid: 'Não paga',
  incomplete: 'Incompleta',
  incomplete_expired: 'Expirada',
};

const STATUS_TONE: Record<string, Tone> = {
  trialing: 'primary',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
};

function formatBRL(cents?: number): string {
  if (cents === undefined || cents === null) return '—';
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatShortDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

/**
 * Dashboard por perfil (Fase 26): usuários de acesso total (admin/legados)
 * veem a visão geral do deploy; os demais veem uma seção por perfil que
 * acumulam (monitor, gestão de vendas, vendedor, financeiro, gestão de
 * projetos) — empilhadas quando o usuário tem mais de um perfil.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const fullAccess = isFullAccess(user);

  if (!fullAccess) {
    return <RoleDashboards />;
  }
  return <AdminDashboard />;
}

function RoleDashboards() {
  const { user } = useAuth();
  const roles = userRoles(user);

  const sections: Array<{ key: string; title: string; node: React.ReactNode }> = [];
  if (roles.includes('monitor')) {
    sections.push({ key: 'monitor', title: 'Agendamento', node: <MonitorDashboard /> });
  }
  if (roles.includes('gestao_vendas')) {
    sections.push({
      key: 'gestao_vendas',
      title: 'Gestão de vendas',
      node: <ProspectDashboard />,
    });
  } else if (roles.includes('vendedor')) {
    sections.push({ key: 'vendedor', title: 'Minhas vendas', node: <VendedorDashboard /> });
  }
  if (roles.includes('financeiro')) {
    sections.push({
      key: 'financeiro',
      title: 'Financeiro',
      node: <PlaceholderDashboard perfil="Financeiro" />,
    });
  }
  if (roles.includes('gestao_projetos')) {
    sections.push({
      key: 'gestao_projetos',
      title: 'Gestão de projetos',
      node: <PlaceholderDashboard perfil="Gestão de projetos" />,
    });
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-fg">
          Olá, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-fg-2 mt-1">
          {roles.map((r) => ROLE_LABEL[r] ?? r).join(' · ')}
        </p>
      </div>

      {sections.length === 0 ? (
        <PlaceholderDashboard perfil="Sem perfil configurado" />
      ) : (
        <div className="space-y-10">
          {sections.map((s) => (
            <section key={s.key}>
              {sections.length > 1 && (
                <h2 className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
                  {s.title}
                </h2>
              )}
              {s.node}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDashboard() {
  const { user } = useAuth();
  const chart = useChartTheme();
  const [enabled, setEnabled] = useState<ModuleInfo[]>([]);
  const [all, setAll] = useState<ModuleInfo[]>([]);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([meAPI.modules(), dashboardAPI.overview()])
      .then(([modulesResp, overviewResp]) => {
        setEnabled(modulesResp.enabled);
        setAll(modulesResp.all);
        setOverview(overviewResp);
      })
      .catch((err) => {
        console.error('[Dashboard] erro ao carregar overview:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const enabledIds = new Set(enabled.map((m) => m.id));
  const disabled = all.filter((m) => !enabledIds.has(m.id));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-fg">
            Olá, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-fg-2 mt-1">
            {overview ? (
              <>Visão geral de <strong className="text-fg-2">{overview.client}</strong></>
            ) : (
              'Visão geral do seu deploy'
            )}
          </p>
        </div>
        {overview && (
          <SubscriptionPill billing={overview.billing} />
        )}
      </div>

      {/* Stats grid */}
      {loading ? (
        <StatsSkeleton />
      ) : overview ? (
        <StatsGrid overview={overview} />
      ) : null}

      {/* Próximos agendamentos chart */}
      {overview?.agendamento && overview.agendamento.proximos.length > 0 && (
        <Card className="p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={18} className="text-primary" />
            <h2 className="text-[19px] font-semibold tracking-[-0.022em] text-fg">
              Agendamentos próximos 14 dias
            </h2>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={overview.agendamento.proximos.map((p) => ({
                  data: formatShortDate(p.data),
                  count: p.count,
                }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" />
                <XAxis dataKey="data" stroke={chart.axis} fontSize={11} />
                <YAxis stroke={chart.axis} fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.tooltipBorder}`,
                    borderRadius: '8px',
                    color: chart.tooltipText,
                  }}
                  cursor={{ fill: chart.grid, fillOpacity: 0.5 }}
                />
                <Bar dataKey="count" fill={chart.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Módulos cards */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
          Módulos habilitados
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {enabled.map((m) => {
            const meta = MODULE_META[m.id];
            return (
              <NavLink
                key={m.id}
                to={meta?.to ?? '/'}
                className="group rounded-xl border border-line bg-surface p-5 shadow-xs transition-[box-shadow,border-color] duration-[120ms] hover:border-faint hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
                    {meta?.icon}
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-faint group-hover:text-primary transition"
                  />
                </div>
                <h3 className="text-lg font-semibold text-fg">{m.displayName}</h3>
                <p className="text-sm text-fg-2 mt-1 line-clamp-2">{m.description}</p>
              </NavLink>
            );
          })}
          <NavLink
            to="/billing"
            className="group rounded-xl border border-line bg-surface p-5 shadow-xs transition-[box-shadow,border-color] duration-[120ms] hover:border-faint hover:shadow-md"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
                <CreditCard size={24} />
              </div>
              <ArrowRight
                size={18}
                className="text-faint group-hover:text-primary transition"
              />
            </div>
            <h3 className="text-lg font-semibold text-fg">Assinatura</h3>
            <p className="text-sm text-fg-2 mt-1">
              Veja seu plano, faturas e gerencie cobranças.
            </p>
          </NavLink>
        </div>
      </div>

      {/* Módulos não habilitados */}
      {disabled.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-muted uppercase tracking-wider mb-3">
            Não habilitados
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {disabled.map((m) => {
              const meta = MODULE_META[m.id];
              return (
                <div
                  key={m.id}
                  className="rounded-xl border border-line bg-surface p-5 opacity-50"
                >
                  <div className="w-12 h-12 rounded-xl bg-hover/50 text-faint flex items-center justify-center mb-3">
                    {meta?.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-fg-2">{m.displayName}</h3>
                  <p className="text-sm text-muted mt-1 line-clamp-2">{m.description}</p>
                  <p className="text-xs text-faint mt-2 italic">
                    Não habilitado neste deploy.
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Sub-components =====

function SubscriptionPill({ billing }: { billing: DashboardOverview['billing'] }) {
  if (!billing.hasSubscription) {
    return (
      <NavLink to="/billing" className="transition hover:opacity-80">
        <Badge tone="warning" icon={<AlertCircle size={13} className="flex-none" />}>
          Sem assinatura
        </Badge>
      </NavLink>
    );
  }

  const valor =
    billing.monthlyAmountCents !== undefined
      ? ` · ${formatBRL(billing.monthlyAmountCents)}/mês`
      : '';
  return (
    <NavLink to="/billing" className="transition hover:opacity-80">
      <Badge
        tone={STATUS_TONE[billing.status ?? ''] ?? 'neutral'}
        icon={<CreditCard size={13} className="flex-none" />}
      >
        {(STATUS_LABEL[billing.status ?? ''] ?? billing.status) + valor}
      </Badge>
    </NavLink>
  );
}

function StatsGrid({ overview }: { overview: DashboardOverview }) {
  const cards: Array<{
    label: string;
    value: string | number;
    sublabel?: string;
    icon: React.ReactNode;
    accent: string;
  }> = [];

  if (overview.agendamento) {
    cards.push({
      label: 'Agendamentos do mês',
      value: overview.agendamento.total,
      sublabel: `${overview.agendamento.pendentes} pendentes · ${overview.agendamento.confirmados} confirmados`,
      icon: <Clock size={20} />,
      accent: 'text-primary bg-primary-soft/30',
    });
  }

  if (overview.prospect) {
    cards.push({
      label: 'Contratos prospect',
      value: overview.prospect.total,
      sublabel: `${overview.prospect.hoje} hoje · ${overview.prospect.pending} pendentes`,
      icon: <FileText size={20} />,
      accent: 'text-info bg-info-soft/30',
    });
  }

  if (overview.central) {
    cards.push({
      label: 'Devices Central',
      value: overview.central.devicesAtivos,
      sublabel: 'dispositivos ativos',
      icon: <Users size={20} />,
      accent: 'text-success bg-success-soft/30',
    });
    cards.push({
      label: 'Push hoje',
      value: overview.central.notificacoesHoje,
      sublabel: `${overview.central.notificacoesTotal} total`,
      icon: <Send size={20} />,
      accent: 'text-info bg-info-soft/30',
    });
  }

  if (overview.billing.hasSubscription && overview.billing.monthlyAmountCents !== undefined) {
    cards.push({
      label: 'Mensal',
      value: formatBRL(overview.billing.monthlyAmountCents),
      sublabel: overview.billing.currentPeriodEnd
        ? `renova em ${formatShortDate(overview.billing.currentPeriodEnd)}`
        : 'sem ciclo definido',
      icon: <TrendingUp size={20} />,
      accent: 'text-success bg-success-soft/30',
    });
  }

  if (overview.billing.nextInvoice) {
    cards.push({
      label: 'Próxima fatura',
      value: formatBRL(overview.billing.nextInvoice.amountDueCents),
      sublabel: overview.billing.nextInvoice.dueDate
        ? `vence ${formatShortDate(overview.billing.nextInvoice.dueDate)}`
        : 'sem vencimento',
      icon: <CreditCard size={20} />,
      accent: 'text-warning bg-warning-soft/30',
    });
  }

  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
      {cards.map((c) => (
        <Card key={c.label} className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted">{c.label}</span>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.accent}`}>
              {c.icon}
            </div>
          </div>
          <div className="text-2xl font-semibold tracking-[-0.03em] tabular-nums text-fg">
            {c.value}
          </div>
          {c.sublabel && (
            <div className="text-xs text-muted mt-1 truncate">{c.sublabel}</div>
          )}
        </Card>
      ))}
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
      {[...Array(6)].map((_, i) => (
        <Card key={i} className="p-4">
          <Skeleton className="h-3 w-2/3 mb-3" />
          <Skeleton className="h-7 w-1/2" />
        </Card>
      ))}
    </div>
  );
}
